// Faq3.js - Contains functions for creating a FAQ section with cards layout
import { v4 as uuidv4 } from 'uuid';
import { DEVICES } from '../../constants/breakpoints';

/**
 * Creates a Frequently Asked Questions section with cards layout
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the FAQ section is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The FAQ section elements
 */
export const createFaqElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const faqSectionClass = 'faq-section-cards';
  const faqContainerClass = 'faq-container-cards';
  const faqHeaderClass = 'faq-header-cards';
  const faqTitleClass = 'faq-title-cards';
  const faqSubtitleClass = 'faq-subtitle-cards';
  const faqGridClass = 'faq-grid-cards';
  const faqCardClass = 'faq-card';
  const faqCardActiveClass = 'faq-card-active';
  const faqCategoryClass = 'faq-category';
  const faqQuestionClass = 'faq-question-cards';
  const faqAnswerClass = 'faq-answer-cards';
  const faqAnswerActiveClass = 'faq-answer-active-cards';
  
  // Define styles
  // Desktop Styles (default)
  const faqSectionStyles = {
    position: 'relative',
    paddingTop: '80px',
    paddingBottom: '80px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#faf7f5',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    overflow: 'hidden'
  };

  const faqContainerStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    maxWidth: '1200px',
    marginLeft: 'auto',
    marginRight: 'auto'
  };

  const faqHeaderStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    marginBottom: '50px',
    maxWidth: '800px'
  };

  const faqTitleStyles = {
    fontSize: '42px',
    fontWeight: '800',
    marginBottom: '20px',
    color: '#2d1d0e',
    lineHeight: '1.2',
  };

  const faqSubtitleStyles = {
    fontSize: '18px',
    lineHeight: '1.6',
    color: '#5c4d3d',
    maxWidth: '700px'
  };

  const faqGridStyles = {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '24px',
    width: '100%'
  };

  const faqCardStyles = {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    padding: '32px',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.05)',
    transition: 'all 0.3s ease',
    border: '1px solid #eee6e2',
    display: 'flex',
    flexDirection: 'column',
    cursor: 'pointer',
    '&:hover': {
      transform: 'translateY(-5px)',
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)'
    }
  };

  const faqCardActiveStyles = {
    borderColor: '#e27730',
    boxShadow: '0 8px 30px rgba(226, 119, 48, 0.15)'
  };

  const faqCategoryStyles = {
    display: 'inline-block',
    padding: '6px 12px',
    backgroundColor: '#fff0e6',
    color: '#e27730',
    fontSize: '12px',
    fontWeight: '600',
    borderRadius: '20px',
    marginBottom: '16px',
    textTransform: 'uppercase',
    letterSpacing: '1px'
  };

  const faqQuestionStyles = {
    fontSize: '20px',
    fontWeight: '700',
    marginBottom: '16px',
    color: '#2d1d0e',
    lineHeight: '1.4'
  };

  const faqAnswerStyles = {
    fontSize: '16px',
    lineHeight: '1.6',
    color: '#5c4d3d',
    height: '0',
    overflow: 'hidden',
    opacity: '0',
    maxHeight: '0',
    transition: 'all 0.3s ease',
    marginBottom: '0'
  };

  const faqAnswerActiveStyles = {
    height: 'auto',
    opacity: '1',
    maxHeight: '1000px',
    marginTop: '16px'
  };

  // Tablet Styles
  const tabletFaqGridStyles = {
    gridTemplateColumns: '1fr'
  };

  const tabletFaqTitleStyles = {
    fontSize: '36px'
  };

  // Mobile Styles
  const mobileFaqTitleStyles = {
    fontSize: '30px'
  };

  const mobileFaqCardStyles = {
    padding: '24px'
  };

  const mobileFaqQuestionStyles = {
    fontSize: '18px'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(faqSectionClass, faqSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(faqContainerClass, faqContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(faqHeaderClass, faqHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(faqTitleClass, faqTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(faqSubtitleClass, faqSubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(faqGridClass, faqGridStyles);
    newRegistry[DEVICES.DESKTOP].set(faqCardClass, faqCardStyles);
    newRegistry[DEVICES.DESKTOP].set(faqCardActiveClass, faqCardActiveStyles);
    newRegistry[DEVICES.DESKTOP].set(faqCategoryClass, faqCategoryStyles);
    newRegistry[DEVICES.DESKTOP].set(faqQuestionClass, faqQuestionStyles);
    newRegistry[DEVICES.DESKTOP].set(faqAnswerClass, faqAnswerStyles);
    newRegistry[DEVICES.DESKTOP].set(faqAnswerActiveClass, faqAnswerActiveStyles);
    
    // Tablet styles
    newRegistry[DEVICES.TABLET].set(faqGridClass, tabletFaqGridStyles);
    newRegistry[DEVICES.TABLET].set(faqTitleClass, tabletFaqTitleStyles);
    
    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(faqTitleClass, mobileFaqTitleStyles);
    newRegistry[DEVICES.MOBILE].set(faqCardClass, mobileFaqCardStyles);
    newRegistry[DEVICES.MOBILE].set(faqQuestionClass, mobileFaqQuestionStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    faqSectionClass,
    faqContainerClass,
    faqHeaderClass,
    faqTitleClass,
    faqSubtitleClass,
    faqGridClass,
    faqCardClass,
    faqCardActiveClass,
    faqCategoryClass,
    faqQuestionClass,
    faqAnswerClass,
    faqAnswerActiveClass
  ]));

  // Generate IDs for elements
  const sectionId = Date.now();
  const containerId = Date.now() + 1;
  const headerId = Date.now() + 2;
  const titleId = Date.now() + 3;
  const subtitleId = Date.now() + 4;
  const gridId = Date.now() + 5;
  
  // FAQ Cards
  const card1Id = Date.now() + 6;
  const category1Id = Date.now() + 7;
  const question1Id = Date.now() + 8;
  const answer1Id = Date.now() + 9;
  
  const card2Id = Date.now() + 10;
  const category2Id = Date.now() + 11;
  const question2Id = Date.now() + 12;
  const answer2Id = Date.now() + 13;
  
  const card3Id = Date.now() + 14;
  const category3Id = Date.now() + 15;
  const question3Id = Date.now() + 16;
  const answer3Id = Date.now() + 17;
  
  const card4Id = Date.now() + 18;
  const category4Id = Date.now() + 19;
  const question4Id = Date.now() + 20;
  const answer4Id = Date.now() + 21;

  const card5Id = Date.now() + 22;
  const category5Id = Date.now() + 23;
  const question5Id = Date.now() + 24;
  const answer5Id = Date.now() + 25;

  const card6Id = Date.now() + 26;
  const category6Id = Date.now() + 27;
  const question6Id = Date.now() + 28;
  const answer6Id = Date.now() + 29;

  const newElements = [
    // Main section
    {
      id: sectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'FAQ Section Cards',
      parentId: null,
      className: faqSectionClass,
      styles: {},
      children: [containerId],
      text: '',
      attributes: {
        'data-element': 'faq-section-cards',
        'data-component': 'section',
        'data-section-type': 'faq',
        'data-page-id': currentPageId
      }
    },
    
    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'FAQ Container',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: faqContainerClass,
      styles: {},
      children: [headerId, gridId],
      text: '',
      attributes: {
        'data-element': 'faq-container-cards'
      }
    },
    
    // Header
    {
      id: headerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'FAQ Header',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: faqHeaderClass,
      styles: {},
      children: [titleId, subtitleId],
      text: '',
      attributes: {
        'data-element': 'faq-header-cards'
      }
    },
    
    // Title
    {
      id: titleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h2',
      name: 'FAQ Title',
      parentId: headerId,
      hierarchyName: getHierarchyName('h2'),
      className: faqTitleClass,
      styles: {},
      children: [],
      text: 'Questions You May Have',
      attributes: {
        'data-element': 'faq-title-cards'
      }
    },
    
    // Subtitle
    {
      id: subtitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'FAQ Subtitle',
      parentId: headerId,
      hierarchyName: getHierarchyName('p'),
      className: faqSubtitleClass,
      styles: {},
      children: [],
      text: 'Browse through these frequently asked questions to find answers to common queries about our products and services.',
      attributes: {
        'data-element': 'faq-subtitle-cards'
      }
    },
    
    // FAQ Grid container
    {
      id: gridId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'FAQ Grid',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: faqGridClass,
      styles: {},
      children: [card1Id, card2Id, card3Id, card4Id, card5Id, card6Id],
      text: '',
      attributes: {
        'data-element': 'faq-grid-cards'
      }
    },
    
    // FAQ Card 1
    {
      id: card1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'FAQ Card 1',
      parentId: gridId,
      hierarchyName: getHierarchyName('div'),
      className: faqCardClass,
      styles: {},
      children: [category1Id, question1Id, answer1Id],
      text: '',
      attributes: {
        'data-element': 'faq-card',
        'data-card-index': '1'
      },
      interactions: {
        events: [
          {
            id: Date.now() + 1001,
            type: 'click',
            sequence: 'first',
            action: 'toggleClass',
            value: {
              targetId: answer1Id,
              className: faqAnswerActiveClass
            },
            label: `1st Click: Toggle Class: ${faqAnswerActiveClass} on DIV ${answer1Id}`
          }
        ]
      }
    },
    
    // FAQ Category 1
    {
      id: category1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'FAQ Category 1',
      parentId: card1Id,
      hierarchyName: getHierarchyName('span'),
      className: faqCategoryClass,
      styles: {},
      children: [],
      text: 'Services',
      attributes: {
        'data-element': 'faq-category'
      }
    },
    
    // FAQ Question 1
    {
      id: question1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'FAQ Question 1',
      parentId: card1Id,
      hierarchyName: getHierarchyName('h3'),
      className: faqQuestionClass,
      styles: {},
      children: [],
      text: 'What services do you offer?',
      attributes: {
        'data-element': 'faq-question-cards'
      }
    },
    
    // FAQ Answer 1
    {
      id: answer1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'FAQ Answer 1',
      parentId: card1Id,
      hierarchyName: getHierarchyName('p'),
      className: faqAnswerClass,
      styles: {},
      children: [],
      text: 'We offer a comprehensive range of digital services including web design and development, mobile app development, e-commerce solutions, digital marketing, and brand strategy.',
      attributes: {
        'data-element': 'faq-answer-cards'
      }
    },
    
    // FAQ Card 2
    {
      id: card2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'FAQ Card 2',
      parentId: gridId,
      hierarchyName: getHierarchyName('div'),
      className: faqCardClass,
      styles: {},
      children: [category2Id, question2Id, answer2Id],
      text: '',
      attributes: {
        'data-element': 'faq-card',
        'data-card-index': '2'
      },
      interactions: {
        events: [
          {
            id: Date.now() + 1003,
            type: 'click',
            sequence: 'first',
            action: 'toggleClass',
            value: {
              targetId: answer2Id,
              className: faqAnswerActiveClass
            },
            label: `1st Click: Toggle Class: ${faqAnswerActiveClass} on DIV ${answer2Id}`
          }
        ]
      }
    },
    
    // FAQ Category 2
    {
      id: category2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'FAQ Category 2',
      parentId: card2Id,
      hierarchyName: getHierarchyName('span'),
      className: faqCategoryClass,
      styles: {},
      children: [],
      text: 'Pricing',
      attributes: {
        'data-element': 'faq-category'
      }
    },
    
    // FAQ Question 2
    {
      id: question2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'FAQ Question 2',
      parentId: card2Id,
      hierarchyName: getHierarchyName('h3'),
      className: faqQuestionClass,
      styles: {},
      children: [],
      text: 'How much does your service cost?',
      attributes: {
        'data-element': 'faq-question-cards'
      }
    },
    
    // FAQ Answer 2
    {
      id: answer2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'FAQ Answer 2',
      parentId: card2Id,
      hierarchyName: getHierarchyName('p'),
      className: faqAnswerClass,
      styles: {},
      children: [],
      text: 'Our pricing is tailored to your specific needs and the scope of your project. We offer flexible pricing models including fixed project rates, hourly rates, and retainer packages.',
      attributes: {
        'data-element': 'faq-answer-cards'
      }
    },
    
    // FAQ Card 3
    {
      id: card3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'FAQ Card 3',
      parentId: gridId,
      hierarchyName: getHierarchyName('div'),
      className: faqCardClass,
      styles: {},
      children: [category3Id, question3Id, answer3Id],
      text: '',
      attributes: {
        'data-element': 'faq-card',
        'data-card-index': '3'
      },
      interactions: {
        events: [
          {
            id: Date.now() + 1005,
            type: 'click',
            sequence: 'first',
            action: 'toggleClass',
            value: {
              targetId: answer3Id,
              className: faqAnswerActiveClass
            },
            label: `1st Click: Toggle Class: ${faqAnswerActiveClass} on DIV ${answer3Id}`
          }
        ]
      }
    },
    
    // FAQ Category 3
    {
      id: category3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'FAQ Category 3',
      parentId: card3Id,
      hierarchyName: getHierarchyName('span'),
      className: faqCategoryClass,
      styles: {},
      children: [],
      text: 'Timelines',
      attributes: {
        'data-element': 'faq-category'
      }
    },
    
    // FAQ Question 3
    {
      id: question3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'FAQ Question 3',
      parentId: card3Id,
      hierarchyName: getHierarchyName('h3'),
      className: faqQuestionClass,
      styles: {},
      children: [],
      text: 'How long does it take to complete a project?',
      attributes: {
        'data-element': 'faq-question-cards'
      }
    },
    
    // FAQ Answer 3
    {
      id: answer3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'FAQ Answer 3',
      parentId: card3Id,
      hierarchyName: getHierarchyName('p'),
      className: faqAnswerClass,
      styles: {},
      children: [],
      text: 'Project timelines vary depending on complexity and scope. A simple website might take 2-4 weeks, while a complex e-commerce platform could take 3-6 months.',
      attributes: {
        'data-element': 'faq-answer-cards'
      }
    },
    
    // FAQ Card 4
    {
      id: card4Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'FAQ Card 4',
      parentId: gridId,
      hierarchyName: getHierarchyName('div'),
      className: faqCardClass,
      styles: {},
      children: [category4Id, question4Id, answer4Id],
      text: '',
      attributes: {
        'data-element': 'faq-card',
        'data-card-index': '4'
      },
      interactions: {
        events: [
          {
            id: Date.now() + 1007,
            type: 'click',
            sequence: 'first',
            action: 'toggleClass',
            value: {
              targetId: answer4Id,
              className: faqAnswerActiveClass
            },
            label: `1st Click: Toggle Class: ${faqAnswerActiveClass} on DIV ${answer4Id}`
          }
        ]
      }
    },
    
    // FAQ Category 4
    {
      id: category4Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'FAQ Category 4',
      parentId: card4Id,
      hierarchyName: getHierarchyName('span'),
      className: faqCategoryClass,
      styles: {},
      children: [],
      text: 'Support',
      attributes: {
        'data-element': 'faq-category'
      }
    },
    
    // FAQ Question 4
    {
      id: question4Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'FAQ Question 4',
      parentId: card4Id,
      hierarchyName: getHierarchyName('h3'),
      className: faqQuestionClass,
      styles: {},
      children: [],
      text: 'Do you offer ongoing support and maintenance?',
      attributes: {
        'data-element': 'faq-question-cards'
      }
    },
    
    // FAQ Answer 4
    {
      id: answer4Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'FAQ Answer 4',
      parentId: card4Id,
      hierarchyName: getHierarchyName('p'),
      className: faqAnswerClass,
      styles: {},
      children: [],
      text: 'Yes, we offer comprehensive support and maintenance packages to ensure your digital solutions continue to perform optimally, including regular updates and technical assistance.',
      attributes: {
        'data-element': 'faq-answer-cards'
      }
    },
    
    // FAQ Card 5
    {
      id: card5Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'FAQ Card 5',
      parentId: gridId,
      hierarchyName: getHierarchyName('div'),
      className: faqCardClass,
      styles: {},
      children: [category5Id, question5Id, answer5Id],
      text: '',
      attributes: {
        'data-element': 'faq-card',
        'data-card-index': '5'
      },
      interactions: {
        events: [
          {
            id: Date.now() + 1009,
            type: 'click',
            sequence: 'first',
            action: 'toggleClass',
            value: {
              targetId: answer5Id,
              className: faqAnswerActiveClass
            },
            label: `1st Click: Toggle Class: ${faqAnswerActiveClass} on DIV ${answer5Id}`
          }
        ]
      }
    },
    
    // FAQ Category 5
    {
      id: category5Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'FAQ Category 5',
      parentId: card5Id,
      hierarchyName: getHierarchyName('span'),
      className: faqCategoryClass,
      styles: {},
      children: [],
      text: 'Getting Started',
      attributes: {
        'data-element': 'faq-category'
      }
    },
    
    // FAQ Question 5
    {
      id: question5Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'FAQ Question 5',
      parentId: card5Id,
      hierarchyName: getHierarchyName('h3'),
      className: faqQuestionClass,
      styles: {},
      children: [],
      text: 'How do I get started with your services?',
      attributes: {
        'data-element': 'faq-question-cards'
      }
    },
    
    // FAQ Answer 5
    {
      id: answer5Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'FAQ Answer 5',
      parentId: card5Id,
      hierarchyName: getHierarchyName('p'),
      className: faqAnswerClass,
      styles: {},
      children: [],
      text: 'Getting started is easy! Simply contact us through our website form, email, or phone. We\'ll schedule a free consultation to discuss your needs and goals.',
      attributes: {
        'data-element': 'faq-answer-cards'
      }
    },
    
    // FAQ Card 6
    {
      id: card6Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'FAQ Card 6',
      parentId: gridId,
      hierarchyName: getHierarchyName('div'),
      className: faqCardClass,
      styles: {},
      children: [category6Id, question6Id, answer6Id],
      text: '',
      attributes: {
        'data-element': 'faq-card',
        'data-card-index': '6'
      },
      interactions: {
        events: [
          {
            id: Date.now() + 1011,
            type: 'click',
            sequence: 'first',
            action: 'toggleClass',
            value: {
              targetId: answer6Id,
              className: faqAnswerActiveClass
            },
            label: `1st Click: Toggle Class: ${faqAnswerActiveClass} on DIV ${answer6Id}`
          }
        ]
      }
    },
    
    // FAQ Category 6
    {
      id: category6Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'FAQ Category 6',
      parentId: card6Id,
      hierarchyName: getHierarchyName('span'),
      className: faqCategoryClass,
      styles: {},
      children: [],
      text: 'Payment',
      attributes: {
        'data-element': 'faq-category'
      }
    },
    
    // FAQ Question 6
    {
      id: question6Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'FAQ Question 6',
      parentId: card6Id,
      hierarchyName: getHierarchyName('h3'),
      className: faqQuestionClass,
      styles: {},
      children: [],
      text: 'What payment methods do you accept?',
      attributes: {
        'data-element': 'faq-question-cards'
      }
    },
    
    // FAQ Answer 6
    {
      id: answer6Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'FAQ Answer 6',
      parentId: card6Id,
      hierarchyName: getHierarchyName('p'),
      className: faqAnswerClass,
      styles: {},
      children: [],
      text: 'We accept all major credit cards, bank transfers, and digital payment methods. For larger projects, we offer flexible payment schedules with milestone-based billing.',
      attributes: {
        'data-element': 'faq-answer-cards'
      }
    }
  ];

  return {
    faqSection: newElements[0],
    elements: newElements
  };
};

export default createFaqElements; 